import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Plus, Clock, Video } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { PageContainer } from '@/components/layout/PageContainer'
import { ResponsiveCardGrid } from '@/components/layout/ResponsiveCardGrid'
import { meetingService } from '@/services/meetingService'
import { matchingService } from '@/services/matchingService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import MeetingRequestModal from '@/components/meetings/MeetingRequestModal'
import MeetingInviteCard from '@/components/meetings/MeetingInviteCard'
import { MeetingRequest, Meeting } from '@/types/meeting'

const MEETING_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled'
}

const MEETING_STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-green-100 text-green-700',
  in_progress: 'bg-accent-100 text-accent-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  rescheduled: 'bg-yellow-100 text-yellow-700'
}

const MEETING_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  regular_checkin: 'Regular Check-in',
  project_review: 'Project Review',
  career_guidance: 'Career Guidance',
  skill_assessment: 'Skill Assessment',
  goal_setting: 'Goal Setting',
  feedback_session: 'Feedback Session',
  other: 'Other'
}

type MeetingsTab = 'pending' | 'upcoming' | 'past'

const UPCOMING_STATUSES = ['scheduled', 'in_progress']
const PAST_STATUSES = ['completed', 'cancelled', 'rescheduled']

const MeetingsPage: React.FC = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState<MeetingsTab>('pending')
  const [requests, setRequests] = useState<MeetingRequest[]>([])
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [namesById, setNamesById] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const [pendingRequests, confirmedMeetings, relationships] = await Promise.all([
        meetingService.getMeetingRequests('pending'),
        meetingService.getMeetings(),
        matchingService.getMyRelationships()
      ])
      setRequests(pendingRequests)
      setMeetings(confirmedMeetings)

      const names: Record<string, string> = {}
      relationships.forEach(r => {
        if (r.mentor) names[r.mentor.id] = `${r.mentor.firstName} ${r.mentor.lastName}`
        if (r.mentee) names[r.mentee.id] = `${r.mentee.firstName} ${r.mentee.lastName}`
      })
      setNamesById(names)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch meetings'
      })
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const counterpartName = (meeting: Meeting): string | undefined => {
    const counterpartId = meeting.mentorId === user?.id ? meeting.menteeId : meeting.mentorId
    return namesById[counterpartId]
  }

  const incoming = requests.filter(r => r.recipientId === user?.id)
  const outgoing = requests.filter(r => r.requesterId === user?.id)
  const upcoming = meetings
    .filter(m => UPCOMING_STATUSES.includes(m.status))
    .sort((a, b) => new Date(a.scheduledStartTime).getTime() - new Date(b.scheduledStartTime).getTime())
  const past = meetings
    .filter(m => PAST_STATUSES.includes(m.status))
    .sort((a, b) => new Date(b.scheduledStartTime).getTime() - new Date(a.scheduledStartTime).getTime())

  const formatDateTime = (value: string): string =>
    new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })

  const renderOutgoingCard = (request: MeetingRequest) => (
    <Card key={request.id} className="mb-4">
      <CardBody>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-gray-900">
            {MEETING_TYPE_LABELS[request.type] || request.type}
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            Awaiting response
          </span>
        </div>
        {request.message && <p className="text-sm text-gray-600 mb-2">{request.message}</p>}
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Video className="w-4 h-4" />
            {request.platform}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {request.duration} min
          </span>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Proposed times: {request.proposedTimes.map(t => formatDateTime(t.start)).join(', ')}
        </div>
      </CardBody>
    </Card>
  )

  const renderMeetingCard = (meeting: Meeting) => (
    <Link key={meeting.id} to={`/meetings/${meeting.id}`} className="block h-full">
      <Card className="flex h-full flex-col hover:border-primary-300 transition-colors">
        <CardBody className="flex h-full flex-col">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900">
              {MEETING_TYPE_LABELS[meeting.type] || meeting.type}
            </h3>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                MEETING_STATUS_STYLES[meeting.status] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
            </span>
            {meeting.isRecurring && (
              // Nested inside the card's outer Link — stopPropagation keeps
              // this badge's own navigation from also triggering the card's.
              <Link
                to={`/meetings/${meeting.id}/series`}
                onClick={e => e.stopPropagation()}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200"
              >
                Recurring
              </Link>
            )}
          </div>
          {counterpartName(meeting) && (
            <p className="text-sm text-gray-600 mb-1">with {counterpartName(meeting)}</p>
          )}
          <div className="mt-auto flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDateTime(meeting.scheduledStartTime)}
            </span>
            <span className="flex items-center gap-1">
              <Video className="w-4 h-4" />
              {meeting.platform}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {meeting.duration} min
            </span>
          </div>
        </CardBody>
      </Card>
    </Link>
  )

  const renderPendingTab = () => {
    if (requests.length === 0) {
      return (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <h3 className="text-lg font-semibold">No Pending Meeting Requests</h3>
            </CardHeader>
            <CardBody>
              <p className="text-gray-600 mb-4">You don't have any pending meeting requests.</p>
              <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                Request a Meeting
              </Button>
            </CardBody>
          </Card>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Incoming</h2>
          {incoming.length === 0 ? (
            <p className="text-sm text-gray-500">No incoming requests</p>
          ) : (
            incoming.map(r => (
              <MeetingInviteCard key={r.id} request={r} onResponded={fetchData} />
            ))
          )}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Outgoing</h2>
          {outgoing.length === 0 ? (
            <p className="text-sm text-gray-500">No outgoing requests</p>
          ) : (
            outgoing.map(renderOutgoingCard)
          )}
        </div>
      </div>
    )
  }

  const renderUpcomingTab = () => {
    if (upcoming.length === 0) {
      return (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No upcoming meetings yet.</p>
        </div>
      )
    }
    return <ResponsiveCardGrid>{upcoming.map(renderMeetingCard)}</ResponsiveCardGrid>
  }

  const openActionItems = past.flatMap(m =>
    m.actionItems
      .filter(item => !item.isCompleted)
      .map(item => ({ meeting: m, item }))
  )

  const renderOpenActionItems = () => {
    if (openActionItems.length === 0) return null

    return (
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-amber-800 mb-2">
          Open Action Items from Past Meetings
        </h2>
        <ul className="space-y-1">
          {openActionItems.map(({ meeting, item }, index) => (
            <li key={item.id ?? `${meeting.id}-${index}`} className="text-sm text-amber-900">
              <Link to={`/meetings/${meeting.id}`} className="underline hover:no-underline">
                {item.title}
              </Link>
              <span className="text-amber-700"> — assigned to {item.assignedTo}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const renderPastTab = () => {
    if (past.length === 0) {
      return (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No past meetings yet.</p>
        </div>
      )
    }
    return (
      <>
        {renderOpenActionItems()}
        <ResponsiveCardGrid>{past.map(renderMeetingCard)}</ResponsiveCardGrid>
      </>
    )
  }

  const tabs: { id: MeetingsTab; label: string; count: number }[] = [
    { id: 'pending', label: 'Pending', count: requests.length },
    { id: 'upcoming', label: 'Upcoming', count: upcoming.length },
    { id: 'past', label: 'Past', count: past.length }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <PageContainer maxWidth="7xl">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-display text-gray-900">Meetings</h1>
            <p className="mt-2 text-gray-600">Schedule and track meetings with your mentorship counterpart</p>
          </div>
          <Button variant="primary" onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Request Meeting
          </Button>
        </div>

        <div role="tablist" className="flex gap-1 border-b border-gray-200 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : activeTab === 'pending' ? (
          renderPendingTab()
        ) : activeTab === 'upcoming' ? (
          renderUpcomingTab()
        ) : (
          renderPastTab()
        )}
      </PageContainer>

      <MeetingRequestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onMeetingRequested={fetchData}
      />
    </div>
  )
}

export default MeetingsPage
